/**
 * Exam Runner — הרצת המבחן המסכם
 *
 * A one-page form of all drawn questions, graded on the SERVER. Nothing here knows
 * the correct answers — they arrive only in the result payload.
 *
 * Accessibility: each question is a <fieldset>/<legend> with native radio inputs,
 * so arrow keys move within a question and the group is announced as a whole. The
 * "unanswered" error is a role="alert" summary with in-page links to each gap.
 */

import { useMemo, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import type { ExamQuestionPublic } from '../../types'

interface ExamRunnerProps {
  questions: ExamQuestionPublic[]
  passScorePercent: number
  submitting: boolean
  onSubmit: (answers: { questionId: string; selectedIndex: number }[]) => void
}

export default function ExamRunner({
  questions,
  passScorePercent,
  submitting,
  onSubmit,
}: ExamRunnerProps) {
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [showUnanswered, setShowUnanswered] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)

  const unanswered = useMemo(
    () => questions.filter((q) => answers[q.id] === undefined),
    [questions, answers]
  )
  const answeredCount = questions.length - unanswered.length

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (unanswered.length > 0) {
      setShowUnanswered(true)
      // Move focus to the summary so the problem is announced, not just painted.
      requestAnimationFrame(() => errorRef.current?.focus())
      return
    }
    onSubmit(questions.map((q) => ({ questionId: q.id, selectedIndex: answers[q.id] })))
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-800">
        {questions.length} שאלות. ציון עובר: {passScorePercent}%. אפשר לשנות תשובות עד להגשה.
      </p>

      <p className="mt-3 text-sm text-gray-700" aria-live="polite">
        ענית על {answeredCount} מתוך {questions.length} שאלות
      </p>

      {showUnanswered && unanswered.length > 0 && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
            <AlertCircle size={16} aria-hidden="true" />
            נותרו {unanswered.length} שאלות ללא תשובה
          </p>
          <ul className="mt-2 list-inside list-disc text-sm text-red-800">
            {unanswered.map((q) => (
              <li key={q.id}>
                <a href={`#question-${q.id}`} className="underline">
                  שאלה {questions.indexOf(q) + 1}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ol className="mt-6 space-y-6">
        {questions.map((q, index) => (
          <li key={q.id}>
            <fieldset
              id={`question-${q.id}`}
              className="rounded-2xl border border-gray-200 bg-white p-5"
            >
              <legend className="px-1 text-base font-semibold text-gray-900">
                {index + 1}. {q.questionText}
              </legend>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, optIndex) => {
                  const id = `q-${q.id}-${optIndex}`
                  return (
                    <div key={id} className="flex items-start gap-2">
                      <input
                        type="radio"
                        id={id}
                        name={`q-${q.id}`}
                        value={optIndex}
                        checked={answers[q.id] === optIndex}
                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: optIndex }))}
                        className="mt-1 h-4 w-4 accent-primary-600"
                      />
                      <label htmlFor={id} className="cursor-pointer text-sm text-gray-800">
                        {opt.text}
                      </label>
                    </div>
                  )
                })}
              </div>
            </fieldset>
          </li>
        ))}
      </ol>

      <div className="mt-8">
        <button type="submit" disabled={submitting} className="btn btn-primary w-full sm:w-auto">
          {submitting ? 'בודק...' : 'הגשת המבחן'}
        </button>
      </div>
    </form>
  )
}
